/**
 * MH-11 Danh sách bản tin — dựng lại theo bản mẫu CMS (contentList), ba chế độ
 * theo prop `cheDo`, dùng chung `dungTaiDuLieu` và `BangDuLieu`:
 *
 *   - 'ca-nhan'  (mặc định, /ban-tin): Kho nội dung cá nhân — lọc Tên bản tin,
 *     Loại file, Ngày cập nhật (khoảng), Trạng thái; nút lớn "Biên tập nội dung";
 *     thao tác Chỉnh sửa / Xóa / Trình duyệt theo trạng thái và quyền.
 *   - 'xuat-ban' (/ban-tin/xuat-ban): bản tin ĐÃ DUYỆT (lọc `trangThai=da_duyet`
 *     ở máy chủ) — thao tác "Đặt lịch phát" dẫn sang MH-18 kèm `banTinId`.
 *   - 'kho-nguon' (/ban-tin/kho-nguon): Kho thông tin nguồn — bản tin ĐÃ DUYỆT được
 *     chia sẻ tới địa bàn của người dùng (`chiaSe=1`, bảng `ban_tin_chia_se`), thao
 *     tác "Đặt lịch phát" như Xuất bản.
 *
 * Số yêu cầu mạng khi mở màn hình: 1 (danh sách, đã gộp số đếm). Ngưỡng Bảng
 * 12.2 là không quá 5.
 *
 * Theo mẫu khách (05/10/2026): lọc Loại file qua tham số bổ sung `loaiBanTin`, cột
 * Người tạo (`tenNguoiTao`), địa bàn hiện tên, thao tác chỉ còn Chỉnh sửa / Xóa.
 * `tuThoiDiem/denThoiDiem` lọc theo `thoi_gian_san_xuat` (epoch giây), không theo
 * `updated_at` → ô "Ngày cập nhật" đổi ngày sang epoch khi gọi API.
 *
 * Gộp kho (29/10/2026): cột và ô lọc "Loại bản tin" (Âm thanh `1,3` / Hình ảnh `2` / Video `4`,
 * gửi `loaiBanTin` lên máy chủ). "Đặt lịch phát" (lịch cụm loa MH-18) chỉ hiện với bản tin âm thanh.
 */
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viBienTapNoiDung as t } from '../ngon-ngu/vi-bien-tap';
import {
  banTinApi,
  DS_NHOM_LOAI_BAN_TIN,
  LOC_THEO_NHOM_LOAI,
  nhomLoaiBanTin,
  type BoLocBanTin,
  type DongBanTin,
} from '../api/ban-tin-api';
import type { TrangDuLieu } from '../api/thiet-bi-api';
import { dungHoan, dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';
import { NhanChuaCoApi, TheTag, type KieuTag } from '../thanh-phan/MauGiaoDien';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { BieuTuongNoiDung, type TenBieuTuongNoiDung } from './ChonLoaiNoiDung';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import './bien-tap.css';

export const TRE_TIM_KIEM_MS = 400;

const KHOA_LOC = [
  'keyword',
  'trangThai',
  'loaiLinhVuc',
  'loaiBanTin',
  'nguoiTaoId',
  'tuThoiDiem',
  'denThoiDiem',
] as const;

export type CheDoMH11 = 'ca-nhan' | 'xuat-ban' | 'kho-nguon';

export interface MH11Props {
  /** Ghi đè lớp API, dùng cho kiểm thử thành phần. */
  api?: Pick<typeof banTinApi, 'danhSach' | 'nhanBan'> &
    Partial<Pick<typeof banTinApi, 'xoa' | 'trinhDuyet'>>;
  /** Chế độ trang theo menu của bản mẫu CMS: kho cá nhân, xuất bản, kho thông tin nguồn. */
  cheDo?: CheDoMH11;
}

/* --------------------------- Hàm hiển thị dùng chung --------------------------- */

/** Tag trạng thái: được duyệt xanh, chờ duyệt vàng, đã hủy (từ chối) đỏ, còn lại trung tính. */
export function kieuTagTrangThai(tt: DongBanTin['trangThai']): KieuTag {
  if (tt === 'da_duyet' || tt === 'dang_phat' || tt === 'da_phat') return 'ok';
  if (tt === 'chua_duyet') return 'vang';
  if (tt === 'da_huy') return 'do';
  return 'trung-tinh';
}

export function TagTrangThaiBanTin({ trangThai }: { trangThai: DongBanTin['trangThai'] }): ReactNode {
  return (
    <span data-trang-thai-ban-tin={trangThai}>
      <TheTag kieu={kieuTagTrangThai(trangThai)}>{vi.banTin.trangThaiNhan[trangThai] ?? trangThai}</TheTag>
    </span>
  );
}

/** Khẩn cấp đỏ, ưu tiên vàng, thông thường xanh — nhãn chữ luôn đi kèm màu. */
export function TagUuTien({ muc }: { muc: string }): ReactNode {
  const nhan = t.uuTienNhan[muc as keyof typeof t.uuTienNhan] ?? muc;
  const kieu: KieuTag = muc === '1' ? 'do' : muc === '2' ? 'vang' : 'xanh';
  return <TheTag kieu={kieu}>{nhan}</TheTag>;
}

function bieuTuongLoai(loai: string): TenBieuTuongNoiDung {
  if (loai === '3') return 'loa';
  if (loai === '4' || loai === '5') return 'video';
  if (loai === '2') return 'anh';
  return 'tep';
}

export function TenBanTinCoBieuTuong({ d, lienKet }: { d: DongBanTin; lienKet?: string }): ReactNode {
  return (
    <span className="nd-ten">
      <BieuTuongNoiDung ten={bieuTuongLoai(d.loaiBanTin)} />
      <span className="chi-cho-doc-man-hinh">
        {t.loaiBanTinNhan[d.loaiBanTin as keyof typeof t.loaiBanTinNhan] ?? d.loaiBanTin}:
      </span>
      {lienKet ? <Link to={lienKet}>{d.tieuDe}</Link> : <span>{d.tieuDe}</span>}
    </span>
  );
}

/** Nhãn ô "Loại bản tin" (Âm thanh / Hình ảnh / Video) của một dòng. */
export function nhanNhomLoai(loai: string): string {
  const nhom = nhomLoaiBanTin(loai);
  return nhom ? t.nhomLoaiNhan[nhom] : (t.loaiBanTinNhan[loai as keyof typeof t.loaiBanTinNhan] ?? loai);
}

/** Ô lọc "Loại bản tin": giá trị gửi thẳng tham số `loaiBanTin` (nhiều mã cách dấu phẩy). */
export function OChonLoaiBanTin({
  id,
  giaTri,
  onDoi,
}: {
  id: string;
  giaTri: string;
  onDoi: (v: string) => void;
}): ReactNode {
  return (
    <OLoc nhan={t.danhSach.cot.loaiBanTin} htmlFor={id}>
      <select id={id} value={giaTri} onChange={(e) => onDoi(e.target.value)}>
        <option value="">{t.danhSach.tatCa}</option>
        {DS_NHOM_LOAI_BAN_TIN.map((k) => (
          <option key={k} value={LOC_THEO_NHOM_LOAI[k]}>
            {t.nhomLoaiNhan[k]}
          </option>
        ))}
      </select>
    </OLoc>
  );
}

/** Danh mục lưu "tỉnh Phú Thọ"/"xã Sông Lô" — viết hoa chữ đầu khi hiển thị. */
function vietHoaDau(s: string): string {
  return s ? s.charAt(0).toLocaleUpperCase('vi') + s.slice(1) : s;
}

export function thoiGianNgan(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return vi.banTin.chuaCo;
  const hai = (n: number): string => String(n).padStart(2, '0');
  return `${hai(d.getDate())}/${hai(d.getMonth() + 1)}/${d.getFullYear()} ${hai(d.getHours())}:${hai(d.getMinutes())}`;
}

/** Ngày `yyyy-mm-dd` của ô chọn ngày → epoch giây (đầu hoặc cuối ngày, giờ máy). */
function ngaySangEpoch(ngay: string | null, cuoiNgay: boolean): string | undefined {
  if (!ngay) return undefined;
  const ms = new Date(`${ngay}T${cuoiNgay ? '23:59:59' : '00:00:00'}`).getTime();
  return Number.isFinite(ms) ? String(Math.floor(ms / 1000)) : undefined;
}

/** Ô lọc nhãn TRÊN control, cho phép gắn nhãn "Chưa có API" cạnh nhãn. */
export function OLoc({
  nhan,
  htmlFor,
  chuaCoApi,
  children,
}: {
  nhan: string;
  htmlFor?: string;
  chuaCoApi?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="truong-loc">
      <label className="truong-loc__nhan" htmlFor={htmlFor}>
        {nhan}
        {chuaCoApi ? <NhanChuaCoApi /> : null}
      </label>
      {children}
    </div>
  );
}

/* ----------------------------------- Màn hình ----------------------------------- */

export function MH11DanhSachBanTin({ api, cheDo = 'ca-nhan' }: MH11Props = {}): ReactNode {
  const goi = api ?? banTinApi;
  const goiXoa = api?.xoa ?? banTinApi.xoa;
  const goiTrinhDuyet = api?.trinhDuyet ?? banTinApi.trinhDuyet;
  const phien = dungPhien();
  const dieuHuong = useNavigate();
  const [thamSo, datThamSo] = useSearchParams();
  const [oTim, datOTim] = useState(thamSo.get('keyword') ?? '');
  const tuKhoaHoan = dungHoan(oTim, TRE_TIM_KIEM_MS);
  const [dangXuLy, datDangXuLy] = useState<string | null>(null);
  const [canXoa, datCanXoa] = useState<DongBanTin | null>(null);
  const [thongBao, datThongBao] = useState<{ loai: 'ok' | 'loi'; noiDung: string } | null>(null);

  const laKhoNguon = cheDo === 'kho-nguon';
  const laXuatBan = cheDo === 'xuat-ban';
  const trang = Number(thamSo.get('page') ?? 1) || 1;
  const soDong = Number(thamSo.get('pageSize') ?? SO_DONG_MAC_DINH) || SO_DONG_MAC_DINH;
  const nguonId = thamSo.get('nguonId') ?? phien.nguonIdHienTai ?? '';
  // Chế độ xuất bản cố định trạng thái "đã duyệt" ở phía máy chủ.
  const trangThaiLoc = laXuatBan ? 'da_duyet' : (thamSo.get('trangThai') ?? '');

  const dangLoc = useMemo(
    () =>
      KHOA_LOC.some((k) => !(laXuatBan && k === 'trangThai') && (thamSo.get(k) ?? '') !== '') ||
      tuKhoaHoan.trim().length >= 2,
    [thamSo, tuKhoaHoan, laXuatBan],
  );

  const datMotLoc = useCallback(
    (khoa: string, giaTri: string): void => {
      const moi = new URLSearchParams(thamSo);
      if (giaTri) moi.set(khoa, giaTri);
      else moi.delete(khoa);
      moi.set('page', '1');
      datThamSo(moi, { replace: true });
    },
    [thamSo, datThamSo],
  );

  const xoaLoc = useCallback((): void => {
    const moi = new URLSearchParams();
    moi.set('page', '1');
    moi.set('pageSize', String(soDong));
    if (phien.nguonIdHienTai) moi.set('nguonId', phien.nguonIdHienTai);
    datThamSo(moi, { replace: true });
    datOTim('');
  }, [datThamSo, soDong, phien.nguonIdHienTai]);

  const kq = dungTaiDuLieu<TrangDuLieu<DongBanTin>>({
    tai: (signal) =>
      goi.danhSach(
        {
          page: trang,
          pageSize: soDong,
          keyword: tuKhoaHoan.trim().length >= 2 ? tuKhoaHoan.trim() : undefined,
          trangThai: trangThaiLoc || undefined,
          loaiLinhVuc: thamSo.get('loaiLinhVuc') ?? undefined,
          nguoiTaoId: thamSo.get('nguoiTaoId') ?? undefined,
          loaiBanTin: thamSo.get('loaiBanTin') ?? undefined,
          tuThoiDiem: ngaySangEpoch(thamSo.get('tuThoiDiem'), false),
          denThoiDiem: ngaySangEpoch(thamSo.get('denThoiDiem'), true),
          nguonId: laKhoNguon ? undefined : nguonId || undefined,
          chiaSe: laKhoNguon ? '1' : undefined,
        } satisfies BoLocBanTin,
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [
      trang,
      soDong,
      tuKhoaHoan,
      nguonId,
      trangThaiLoc,
      thamSo.get('loaiLinhVuc'),
      thamSo.get('nguoiTaoId'),
      thamSo.get('loaiBanTin'),
      thamSo.get('tuThoiDiem'),
      thamSo.get('denThoiDiem'),
    ],
  });

  const thucHienXoa = useCallback(async (): Promise<void> => {
    const d = canXoa;
    if (!d) return;
    datCanXoa(null);
    datDangXuLy(d.id);
    datThongBao(null);
    try {
      await goiXoa(d.id);
      datThongBao({ loai: 'ok', noiDung: dienVao(t.danhSach.daXoa, { ten: d.tieuDe }) });
      kq.taiLai();
    } catch (e: unknown) {
      datThongBao({
        loai: 'loi',
        noiDung: dienVao(t.danhSach.loiThaoTac, { loi: e instanceof Error ? e.message : '' }),
      });
    } finally {
      datDangXuLy(null);
    }
  }, [canXoa, goiXoa, kq]);

  /** Gửi duyệt ngay từ danh sách (cùng API với nút Gửi duyệt ở trang biên tập). */
  const guiDuyet = useCallback(
    async (d: DongBanTin): Promise<void> => {
      datDangXuLy(d.id);
      datThongBao(null);
      try {
        await goiTrinhDuyet(d.id);
        datThongBao({ loai: 'ok', noiDung: dienVao(t.danhSach.daGuiDuyet, { ten: d.tieuDe }) });
        kq.taiLai();
      } catch (e: unknown) {
        datThongBao({
          loai: 'loi',
          noiDung: dienVao(t.danhSach.loiThaoTac, { loi: e instanceof Error ? e.message : '' }),
        });
      } finally {
        datDangXuLy(null);
      }
    },
    [goiTrinhDuyet, kq],
  );

  const dsDong = kq.duLieu?.items ?? [];
  const sttCua = (d: DongBanTin): number => (trang - 1) * soDong + dsDong.indexOf(d) + 1;

  const coSua = phien.coQuyen('bulletin.update');
  const coTao = phien.coQuyen('bulletin.create');
  const coTrinh = phien.coQuyen('bulletin.submit');

  /**
   * Theo mẫu: chỉ bản tin "Đang thực hiện" có Chỉnh sửa / Xóa; "Đã hủy" chỉ còn Xóa; đã gửi
   * duyệt hoặc đã duyệt thì ô thao tác để trống (máy chủ cũng chặn, xem `ban-tin.service.ts`).
   * Bản tin "Đang thực hiện" (chưa gửi duyệt) có thêm Gửi duyệt; thiếu nội dung thì máy chủ báo lỗi.
   */
  const thaoTacCaNhan = (d: DongBanTin): ReactNode => {
    const dangBan = dangXuLy === d.id;
    const suaDuoc = coSua && d.trangThai === 'tao_moi';
    const xoaDuoc = coSua && (d.trangThai === 'tao_moi' || d.trangThai === 'da_huy');
    const trinhDuoc = coTrinh && d.trangThai === 'tao_moi';
    if (!suaDuoc && !xoaDuoc && !trinhDuoc) return null;
    return (
      <div className="nd-thao-tac bang__hanh-dong">
        {suaDuoc ? (
          <Link to={`/ban-tin/${d.id}/sua`} className="nut nut--chinh nut--nho" data-hanh-dong="chinh-sua">
            {t.danhSach.chinhSua}
          </Link>
        ) : null}
        {trinhDuoc ? (
          <button
            type="button"
            className="nut nut--nho nd-nut-gui-duyet"
            data-hanh-dong="gui-duyet"
            title={t.danhSach.trinhDuyetGoiY}
            disabled={dangBan}
            onClick={() => void guiDuyet(d)}
          >
            {t.danhSach.guiDuyet}
          </button>
        ) : null}
        {xoaDuoc ? (
          <button
            type="button"
            className="nut nut--nguy-hiem nut--nho"
            data-hanh-dong="xoa"
            disabled={dangBan}
            onClick={() => datCanXoa(d)}
          >
            {t.danhSach.xoa}
          </button>
        ) : null}
      </div>
    );
  };

  const thaoTacXuatBan = (d: DongBanTin): ReactNode =>
    nhomLoaiBanTin(d.loaiBanTin) !== 'am-thanh' ? null : (
      <Link
        to={`/lich-phat/moi?banTinId=${encodeURIComponent(d.banTinId)}`}
        className="nut nut--chinh nut--nho"
      >
        <BieuTuongNoiDung ten="tich" co={13} net={2} />
        {t.danhSach.datLichPhat}
      </Link>
    );

  const cot: CotBang<DongBanTin>[] = [
    { khoa: 'stt', nhan: t.danhSach.cot.stt, ve: (d) => <span className="nd-stt">{sttCua(d)}</span> },
    {
      khoa: 'tieuDe',
      nhan: t.danhSach.cot.tenBanTin,
      ve: (d) => <TenBanTinCoBieuTuong d={d} />,
    },
    {
      khoa: 'loaiBanTin',
      nhan: t.danhSach.cot.loaiBanTin,
      ve: (d) => <span data-loai-ban-tin={d.loaiBanTin}>{nhanNhomLoai(d.loaiBanTin)}</span>,
    },
    {
      khoa: 'diaBan',
      nhan: t.danhSach.cot.diaBan,
      ve: (d) => <span>{vietHoaDau(d.tenDiaBanTao ?? d.diaBanTao)}</span>,
    },
    {
      khoa: 'thoiLuong',
      nhan: t.danhSach.cot.thoiLuong,
      ve: (d) => <span>{d.thoiLuong ?? vi.banTin.chuaCo}</span>,
    },
    {
      khoa: 'trangThai',
      nhan: t.danhSach.cot.trangThai,
      ve: (d) => <TagTrangThaiBanTin trangThai={d.trangThai} />,
    },
    ...(laKhoNguon
      ? [
          {
            khoa: 'linhVuc',
            nhan: t.danhSach.cot.linhVucCap1,
            ve: (d: DongBanTin) => (
              <span>
                {vi.banTin.linhVucNhan[d.loaiLinhVuc as keyof typeof vi.banTin.linhVucNhan] ?? d.loaiLinhVuc}
              </span>
            ),
          } satisfies CotBang<DongBanTin>,
        ]
      : []),
    { khoa: 'mucDoUuTien', nhan: t.danhSach.cot.mucDoUuTien, ve: (d) => <TagUuTien muc={d.mucDoUuTien} /> },
    {
      khoa: 'capNhat',
      nhan: t.danhSach.cot.thoiGianCapNhat,
      ve: (d) => <span className="nd-nw">{thoiGianNgan(d.updatedAt)}</span>,
    },
    {
      khoa: 'nguoiTao',
      nhan: t.danhSach.cot.nguoiTao,
      ve: (d) => <span>{d.tenNguoiTao ?? vi.banTin.chuaCo}</span>,
    },
    {
      khoa: 'hanhDong',
      nhan: t.danhSach.cot.thaoTac,
      ve: laXuatBan || laKhoNguon ? thaoTacXuatBan : thaoTacCaNhan,
    },
  ];

  const tieuDe = laKhoNguon
    ? vi.menuCon.khoThongTinNguon
    : laXuatBan
      ? vi.menuCon.xuatBan
      : vi.menuCon.khoNoiDungCaNhan;

  const oTenBanTin = (
    <OLoc nhan={t.danhSach.locTen} htmlFor="bt-tim">
      <span className="nd-o-tim">
        <input
          id="bt-tim"
          type="search"
          placeholder={t.danhSach.locTenGoiY}
          value={oTim}
          onChange={(e) => {
            datOTim(e.target.value);
            datMotLoc('keyword', e.target.value);
          }}
        />
        <button type="button" aria-label={t.danhSach.timKiem} onClick={() => kq.taiLai()}>
          <BieuTuong ten="tim-kiem" co={15} />
        </button>
      </span>
    </OLoc>
  );

  const oLoaiFile = (
    <OChonLoaiBanTin
      id="bt-loai-file"
      giaTri={thamSo.get('loaiBanTin') ?? ''}
      onDoi={(v) => datMotLoc('loaiBanTin', v)}
    />
  );

  const oNgay = (
    <OLoc nhan={t.danhSach.locNgayCapNhat} htmlFor="bt-tu-ngay">
      <span className="nd-khoang-ngay" title={t.danhSach.locNgayGiaiThich}>
        <input
          id="bt-tu-ngay"
          type="date"
          aria-label={`${t.danhSach.locNgayCapNhat} — ${t.danhSach.locTuNgay}`}
          value={thamSo.get('tuThoiDiem') ?? ''}
          onChange={(e) => datMotLoc('tuThoiDiem', e.target.value)}
        />
        <span aria-hidden="true">→</span>
        <input
          id="bt-den-ngay"
          type="date"
          aria-label={`${t.danhSach.locNgayCapNhat} — ${t.danhSach.locDenNgay}`}
          value={thamSo.get('denThoiDiem') ?? ''}
          onChange={(e) => datMotLoc('denThoiDiem', e.target.value)}
        />
      </span>
    </OLoc>
  );

  /* ------------------------- Kho cá nhân và xuất bản ------------------------- */
  return (
    <div className="man-hinh" data-man-hinh="MH-11" data-che-do={cheDo}>
      <h1 className="chi-cho-doc-man-hinh">{tieuDe}</h1>

      <div className="nd-dau">
        <div className="thanh-loc-mau nd-loc" role="search">
          {oTenBanTin}
          {oLoaiFile}
          {laXuatBan || laKhoNguon ? null : (
            <>
              {oNgay}
              <OLoc nhan={t.danhSach.locTrangThai} htmlFor="bt-trang-thai">
                <select
                  id="bt-trang-thai"

                  value={thamSo.get('trangThai') ?? ''}
                  onChange={(e) => datMotLoc('trangThai', e.target.value)}
                >
                  <option value="">{t.danhSach.tatCa}</option>
                  {vi.banTin.locTrangThai.map((o) => (
                    <option key={o.giaTri} value={o.giaTri}>
                      {o.nhan}
                    </option>
                  ))}
                </select>
              </OLoc>
            </>
          )}
        </div>
        {!laXuatBan && !laKhoNguon && coTao ? (
          <Link to="/ban-tin/chon-loai" className="nut nd-nut-bien-tap" data-vung="nut-bien-tap-noi-dung">
            <BieuTuongNoiDung ten="sua" co={16} net={2} />
            {t.danhSach.nutBienTap}
          </Link>
        ) : null}
      </div>

      {thongBao ? (
        <p
          className="nd-thong-bao"
          role={thongBao.loai === 'loi' ? 'alert' : 'status'}
          data-vung="thong-bao-thao-tac"
        >
          <TheTag kieu={thongBao.loai === 'loi' ? 'do' : 'ok'}>{thongBao.noiDung}</TheTag>
        </p>
      ) : null}

      <div className="nd-bang-viet-hoa">
        <BangDuLieu<DongBanTin>
          nhan={tieuDe}
          cot={cot}
          dong={dsDong}
          khoaDong={(d) => d.id}
          nhanDong={(d) => d.tieuDe}
          trangThai={kq.trangThai}
          loi={kq.loi}
          taiLau={kq.taiLau}
          onThuLai={kq.taiLai}
          tong={kq.duLieu?.total ?? 0}
          trang={trang}
          soDongMoiTrang={soDong}
          onDoiTrang={(so) => {
            const moi = new URLSearchParams(thamSo);
            moi.set('page', String(so));
            datThamSo(moi, { replace: true });
          }}
          onDoiSoDong={(s) => {
            const moi = new URLSearchParams(thamSo);
            moi.set('pageSize', String(s));
            moi.set('page', '1');
            datThamSo(moi, { replace: true });
          }}
          dangLoc={dangLoc}
          onXoaLoc={xoaLoc}
          moTaRong={
            laKhoNguon
              ? t.danhSach.rongMoTaKhoNguon
              : laXuatBan
                ? t.danhSach.rongMoTaXuatBan
                : vi.banTin.rongMoTa
          }
          nhanHanhDongRong={laXuatBan || laKhoNguon || !coTao ? undefined : vi.banTin.khaiBaoMoi}
          onHanhDongRong={
            laXuatBan || laKhoNguon || !coTao ? undefined : () => dieuHuong('/ban-tin/chon-loai')
          }
        />
      </div>

      {canXoa ? (
        <HopXacNhan
          tieuDe={t.danhSach.xoaTieuDe}
          heQua={dienVao(t.danhSach.xoaHeQua, { ten: canXoa.tieuDe })}
          nhanThucHien={t.danhSach.xoa}
          onHuy={() => datCanXoa(null)}
          onThucHien={() => void thucHienXoa()}
        />
      ) : null}
    </div>
  );
}
